/**
 * Compliance Pulse — V109-5-4 (PRD G-8; design run `v109-track5-views` round 3, R-a; built-UI fix pass):
 * "What changed" in the program's CURRENT stage (`?view=changes`: Prepare until certified, Operate after;
 * FedRAMP's gated Operate never opens for it).
 *
 *   freshness  "Swept today 06:02 · vs yesterday 06:01", Scope, Sweep again (a partial sweep lists the
 *              source it couldn't read, with its fix)
 *   band       "2 broke since yesterday 06:01 · 1 newly passing · 2 couldn't check · 2 scope changes ·
 *              214 checks unchanged" + ONE filled primary: the top fix (opens its panel in place)
 *   tile       "Sweep checks on N of M controls · vs yesterday" — M is the roll-up's applicable count for
 *              the same tier — the same bar as the roll-up, and "Program: Core · 31 of 57 met →". Met /
 *              Not met / Couldn't check with the change; each count opens the Readiness list filtered
 *              (`?status=…&source=sweep`), whose rows = the count, with a removable "Sweep-checked only ×".
 *   changes    Newly failing, Couldn't check (rows), then Newly passing, Scope changes, Preview as count
 *              rows. A newly failing row's "Fix <resource>" opens the fix panel in place (setting, steps,
 *              Copy, Open finding / its ticket, the affected GovRAMP control / FedRAMP KSI); Escape and
 *              Close return focus to the button that opened it (the row's, when it opened from the URL).
 *
 * Reads (any member): GET /programs/:p/sweep-report (+ the roll-up's readiness reads). Writes: Sweep again
 * (owner / admin, server-enforced). Status messages go through the view's one live region.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Spinner } from '@aigovops/design-system';
import { getProgramManifest, isCouldntCheckReason, type ProgramCard, type ProgramSweepReport, type SweepChange, type SweepCheckState, type SweepSourceStamp } from '@aigovops/core';
import { ApiError } from '../../lib/api';
import { createChangeFinding, getSweepReport, startSweep, sweepKeys } from '../../lib/sweep-api';
import { AskAdmin } from '../AskAdmin';
import { CAUSE_LABEL, programHref, sinceText, STATE_LABEL, unitNoun } from '../../lib/sweep-model';
import { programLabel } from '../../lib/boundary-model';
import { useAnnounce } from '../today/shared';
import { CouldntCheckChip, CouldntCheckFixes } from '../CouldntCheck';
import type { CouldntItem } from '../../lib/couldnt-check';
import { SweepFreshness } from './SweepFreshness';
import { CONTROLS_ANCHOR, useRollupData, type RollupData } from './ProgramRollup';
import './sweep.css';

const CAUSE_GLYPH: Record<string, string> = { environment: '⏦', rules: '▤', couldnt_read: '?' };

/** Plain words for a value read or expected (never a raw JSON dump in the row; the panel shows it). */
export function valueText(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(valueText).join(', ');
  return Object.entries(v as Record<string, unknown>).map(([k, x]) => `${k.replace(/_/g, ' ')}: ${valueText(x)}`).join('; ');
}

const titleOf = (c: SweepChange) => c.title || (c.check_key ? c.check_key.replace(/[_.-]+/g, ' ') : 'A check');
const resName = (c: SweepChange) => c.resource?.name ?? 'the source';
/** The button that opens a change's fix panel (the band's primary first, else the row's). */
const fixOpener = (id: string) => document.querySelector<HTMLElement>(`[data-part="pulse-band"] [aria-controls="fix-${id}"]`)
  ?? document.querySelector<HTMLElement>(`[data-change="${id}"] [aria-controls="fix-${id}"]`);

export function ProgramChanges({ slug, prefix, program, stage, canSweep, readinessHref, card = null }: {
  slug: string; prefix: string; program: string; stage: string; canSweep: boolean;
  /** The Readiness sub-view (the program's control list lives there). */
  readinessHref: string;
  /** The program's card: the tile counts against the roll-up's tier / class (built-UI #1). */
  card?: ProgramCard | null;
}) {
  const q = useQuery({ queryKey: sweepKeys.report(slug, program), queryFn: () => getSweepReport(slug, program), retry: false, staleTime: 30_000 });
  const rollup = useRollupData(slug, getProgramManifest(program), card).data;
  const [live, announce] = useAnnounce();
  const [sp, setSp] = useSearchParams();
  const [openFix, setOpenFix] = useState<string | null>(() => (sp.get('open')?.startsWith('fix-') ? sp.get('open')!.slice(4) : null));
  const opener = useRef<HTMLElement | null>(null);

  // A tile count used to open its list here; it lives on the Readiness view now (one list per status).
  const status = sp.get('status') as SweepCheckState | null;
  if (status && sp.get('source') === 'sweep') return <Navigate replace to={programHref(prefix, program, stage, { status, source: 'sweep' }, CONTROLS_ANCHOR)} />;
  if (q.isLoading) return <Spinner label="Loading what changed" />;
  if (q.error || !q.data) return <Alert variant="error"><p>What changed could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>;
  const r = q.data;
  const label = programLabel(program);
  const base = (extra: Record<string, string | null>, hash?: string) => programHref(prefix, program, stage, { ...extra }, hash);

  if (!r.run) {
    // Built-UI Nit: the freshness line says "No sweep yet" once, with "Sweep now"; the heading names the view.
    return (
      <section className="sw-view" aria-labelledby="pc-h" data-part="changes">
        {live}
        <h3 id="pc-h" className="gp-sec-h">What changed</h3>
        <SweepFreshness slug={slug} prefix={prefix} run={null} compared={null} sources={[]} canSweep={canSweep} announce={announce} id="pc-fresh" />
        <p className="sw-dim">The first sweep reads your connected clouds and code; what changed shows here after the second.</p>
        <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/setup`}>Connect a cloud account</Link>
      </section>
    );
  }

  const close = (id: string | null, el?: HTMLElement | null) => {
    if (id) { opener.current = el ?? opener.current; setOpenFix(id); return; }
    const was = openFix;
    setOpenFix(null);
    const n = new URLSearchParams(sp); n.delete('open'); setSp(n, { replace: true });
    // Opened from the URL (no opener): the row's Fix button takes focus back.
    window.setTimeout(() => ((opener.current?.isConnected ? opener.current : null) ?? (was ? fixOpener(was) : null))?.focus(), 0);
  };
  return <Pulse r={r} slug={slug} prefix={prefix} program={program} canSweep={canSweep} label={label} live={live} announce={announce} base={base}
    readinessHref={readinessHref} rollup={rollup} openFix={openFix} setOpenFix={close} />;
}

function Pulse({ r, slug, prefix, program, canSweep, label, live, announce, base, readinessHref, rollup, openFix, setOpenFix }: {
  r: ProgramSweepReport; slug: string; prefix: string; program: string; canSweep: boolean; label: string; live: React.ReactNode; announce: (m: string) => void;
  base: (extra: Record<string, string | null>, hash?: string) => string; readinessHref: string; rollup: RollupData | null; openFix: string | null; setOpenFix: (id: string | null, el?: HTMLElement | null) => void;
}) {
  const since = sinceText(r.compared_with);
  const broke = r.changes.filter((c) => c.category === 'newly_failing');
  const couldnt = r.changes.filter((c) => c.category === 'unevaluated');
  const passing = r.changes.filter((c) => c.category === 'newly_passing');
  const scope = r.changes.filter((c) => c.category === 'newly_applicable' || c.category === 'no_longer_applicable');
  const total = r.changes.length;
  const top = broke[0] ?? null;
  const now = r.checked.now, prev = r.checked.prev;
  const reached = r.checked.requirements.length - now.not_applicable;
  // Built-UI #1: the denominator is the roll-up's applicable count for the same tier / class.
  const applicable = rollup ? rollup.counts.total - rollup.counts.not_applicable : r.controls_total;
  const delta = (k: SweepCheckState) => (prev ? now[k] - prev[k] : 0);
  const vs = since.replace(/^since /, '');
  const partial = r.run?.state === 'partial';
  const sweepBar = reached ? [['met', now.met], ['not_met', now.not_met], ['couldnt_check', now.couldnt_check]] as const : [];

  return (
    <section className="sw-view" aria-labelledby="pc-h" data-part="changes">
      {live}
      <SweepFreshness slug={slug} prefix={prefix} run={r.run} compared={r.compared_with} sources={r.sources} canSweep={canSweep} announce={announce} id="pc-fresh" />

      <div className="sw-band" data-tone={broke.length ? 'bad' : couldnt.length ? 'warn' : undefined} data-part="pulse-band">
        <span className="sw-band-main">
          {broke.length ? <span className="sw-bad"><span aria-hidden="true">✕ </span>{`${broke.length} broke ${r.compared_with ? since : 'in this sweep'}`}</span> : <b>Nothing broke</b>}
          {passing.length ? <span><b>{passing.length}</b> newly passing</span> : null}
          {/* punch #12: the band's parts are all text (its one action is the primary); the lists below carry the links. */}
          {couldnt.length ? <span><b>{couldnt.length}</b> couldn't check</span> : null}
          {scope.length ? <span><b>{scope.length}</b> scope change{scope.length === 1 ? '' : 's'}</span> : null}
          <span><b>{r.drift.unchanged}</b> check{r.drift.unchanged === 1 ? '' : 's'} unchanged</span>
          {partial ? <span className="sw-dim">from a partial sweep</span> : null}
        </span>
        {top ? <Button variant="primary" aria-expanded={openFix === top.id} aria-controls={`fix-${top.id}`}
          onClick={(e) => setOpenFix(openFix === top.id ? null : top.id, e.currentTarget)}>{`Fix ${resName(top)}`}</Button> : null}
      </div>

      <div className="sw-tile" data-part="pulse-tile">
        <div className="sw-tile-head">
          <h3 id="pc-tile-h">{`Sweep checks on ${reached} of ${applicable} ${unitNoun(r.unit, applicable)}`}<span className="sw-dim">{` · vs ${vs}`}</span></h3>
          <Link className="sw-prog-link" to={readinessHref}>{rollup ? `Program: ${rollup.label} · ${rollup.counts.met} of ${applicable} met` : `Program: ${label} readiness`}<span aria-hidden="true">{' →'}</span></Link>
        </div>
        {sweepBar.length ? (
          <div className="sw-bar" role="img" aria-label={`${now.met} met, ${now.not_met} not met, ${now.couldnt_check} couldn't be checked, of ${reached} with a sweep check`} data-part="tile-bar">
            {sweepBar.filter(([, n]) => n).map(([k, n]) => <span key={k} className="sw-seg" data-k={k} style={{ flexGrow: n }} />)}
          </div>
        ) : null}
        <dl className="sw-tile-counts">
          {(['met', 'not_met', 'couldnt_check'] as const).map((k) => {
            const d = delta(k);
            const worse = k === 'met' ? d < 0 : d > 0;
            return (
              <div key={k}>
                <dt>{STATE_LABEL[k]}{k === 'couldnt_check' ? <> <span className="sw-chip" data-tone="dashed">not counted</span></> : null}</dt>
                <dd>
                  <Link className="sw-count-link" to={base({ status: k, source: 'sweep' }, CONTROLS_ANCHOR)} aria-label={`${now[k]} ${STATE_LABEL[k].toLowerCase()}, sweep-checked`} data-count={k}>{now[k]}</Link>
                  {d ? <span className="sw-delta" data-tone={worse ? 'bad' : 'good'}><span aria-hidden="true">{d < 0 ? '↓' : '↑'}{Math.abs(d)}</span><span className="sr-only">{`${worse ? 'worse' : 'better'} by ${Math.abs(d)}`}</span></span> : null}
                </dd>
              </div>
            );
          })}
        </dl>
        <p className="sw-dim sw-note">
          {now.not_applicable ? <><Link to={base({ status: 'not_applicable', source: 'sweep' }, CONTROLS_ANCHOR)}>{`+${now.not_applicable} not applicable`}</Link><span aria-hidden="true">{' · '}</span></> : null}
          {`Preview: ${r.preview} not counted`}
        </p>
      </div>

      <h3 className="gp-sec-h" id="pc-h" tabIndex={-1}>{`What changed · ${total}`}</h3>
      {total === 0 ? <p className="sw-dim">{`Nothing changed ${since}. ${r.drift.unchanged} checks read the same.`}</p> : null}

      {broke.length ? <>
        <h4 className="sw-h4">{`Newly failing · ${broke.length}`}</h4>
        <ul className="sw-changes" aria-label="Newly failing">
          {broke.map((c) => <ChangeRow key={c.id} c={c} prefix={prefix} program={program} canSweep={canSweep} slug={slug} announce={announce} open={openFix === c.id}
            onFix={(el) => setOpenFix(openFix === c.id ? null : c.id, el)} onClose={() => setOpenFix(null)} />)}
        </ul>
      </> : null}
      {couldnt.length ? <>
        <h4 className="sw-h4" id="pc-cc-h" tabIndex={-1}>{`Couldn't check · ${couldnt.length}`}</h4>
        <ul className="sw-changes" aria-label="Couldn't check">
          {couldnt.map((c) => <ChangeRow key={c.id} c={c} prefix={prefix} program={program} canSweep={canSweep} slug={slug} announce={announce} open={false} onFix={() => undefined} onClose={() => undefined} sources={r.sources} />)}
        </ul>
      </> : null}
      <CountRow title="Newly passing" rows={passing} prefix={prefix} program={program} slug={slug} announce={announce} canSweep={canSweep} />
      <CountRow title="Scope changes" rows={scope} prefix={prefix} program={program} slug={slug} announce={announce} canSweep={canSweep}
        detail={scope.length ? [`${scope.filter((c) => c.category === 'newly_applicable').length} newly applicable`, `${scope.filter((c) => c.category === 'no_longer_applicable').length} no longer applicable`] : []} />
      {r.preview ? (
        <div className="sw-countrow" data-preview="true" data-part="preview-row">
          <span className="sw-countrow-t"><span className="sw-chip" data-tone="dashed">Preview</span>{` ${r.preview} result${r.preview === 1 ? '' : 's'} from checks not yet verified`}<span className="sw-dim"><span aria-hidden="true">{' · '}</span>never counted</span></span>
        </div>
      ) : null}
      <p className="sw-dim sw-foot">{`Unchanged · ${r.drift.unchanged} check${r.drift.unchanged === 1 ? '' : 's'}`}{r.truncated ? ' · the first rows are shown' : ''}</p>
    </section>
  );
}

function CountRow({ title, rows, detail = [], prefix, program, slug, announce, canSweep }: {
  title: string; rows: SweepChange[]; detail?: string[]; prefix: string; program: string; slug: string; announce: (m: string) => void; canSweep: boolean;
}) {
  const [open, setOpen] = useState(false);
  const id = `cr-${title.replace(/\W+/g, '-').toLowerCase()}`;
  if (!rows.length) return null;
  // Built-UI (Medium "Separators"): the count and each detail are their own pieces, "·" hidden from readers.
  return (
    <div className="sw-view">
      <div className="sw-countrow">
        <span className="sw-countrow-t">
          <span className="sw-countrow-h">{title}<span aria-hidden="true">{' · '}</span><span className="sr-only">{': '}</span>{rows.length}</span>
          {detail.map((d) => <span key={d} className="sw-dim"><span aria-hidden="true">{' · '}</span><span className="sr-only">{', '}</span>{d}</span>)}
        </span>
        <button type="button" className="sw-chev" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
          <span aria-hidden="true">{open ? '▾' : '›'}</span>{open ? 'Hide' : 'Show'}<span className="sr-only">{` ${title.toLowerCase()}`}</span>
        </button>
      </div>
      {open ? <ul id={id} className="sw-changes" aria-label={title}>{rows.map((c) => <ChangeRow key={c.id} c={c} prefix={prefix} program={program} canSweep={canSweep} slug={slug} announce={announce} open={false} onFix={() => undefined} onClose={() => undefined} />)}</ul> : null}
    </div>
  );
}

function statusChip(c: SweepChange) {
  if (c.category === 'unevaluated') return <CouldntCheckChip />;
  if (c.category === 'newly_failing' || (c.category === 'newly_applicable' && c.curr_outcome === 'fail')) return <span className="sw-chip" data-tone="bad"><span aria-hidden="true">✕ </span>Not met</span>;
  if (c.category === 'newly_passing' || (c.category === 'newly_applicable' && c.curr_outcome === 'pass')) return <span className="sw-chip" data-tone="ok"><span aria-hidden="true">✓ </span>Met</span>;
  if (c.category === 'no_longer_applicable') return <span className="sw-chip">No longer applies</span>;
  return <span className="sw-chip">New in scope</span>;
}

/**
 * BU-11: the row's source as this sweep read it — a source the sweep couldn't read at all (`error`) needs a
 * new sign-in, so its fix is the one the reason maps to for a connection in error ("Reconnect Cloudflare"),
 * never "Collect again" (which would never run). Same mapping as the freshness line's failed sources.
 */
export function couldntItems(c: SweepChange, sources: readonly SweepSourceStamp[] = []): CouldntItem[] {
  const reason = isCouldntCheckReason(c.reason) ? c.reason : c.reason === 'stale' ? 'stale' : 'collector_error';
  const src = c.connector_id ? sources.find((s) => s.connector_id === c.connector_id) ?? null : null;
  return [{ reason: reason as CouldntItem['reason'], connector_id: c.connector_id, kind: src?.connector_kind ?? null, system_id: null, count: null, resources: c.resource ? [c.resource.name] : [],
    next_collection_at: null, test_key: null, status: src?.status === 'error' ? 'error' : null }];
}

/** "D1 database" (built-UI Nit: the product's own capitalisation, never "d1 database"). */
const TYPE_WORD: Record<string, string> = { d1_database: 'D1 database', gcp_project: 'Project', project: 'Project', organization: 'Organization', account: 'Account', bucket: 'Bucket' };
const typeWordOf = (t: string) => { const k = t.split('.').pop()!; return TYPE_WORD[k] ?? k.replace(/_/g, ' ').replace(/^./, (x) => x.toUpperCase()); };

function ChangeRow({ c, prefix, program, slug, canSweep, announce, open, onFix, onClose, sources = [] }: {
  c: SweepChange; prefix: string; program: string; slug: string; canSweep: boolean; announce: (m: string) => void; open: boolean; onFix: (el: HTMLElement) => void; onClose: () => void;
  /** The sweep's sources (BU-11: a Couldn't-check row's fix follows its source's state). */
  sources?: readonly SweepSourceStamp[];
}) {
  const [ea, setEa] = useState(false);
  const eaId = `ea-${c.id}`;
  const edge = c.category === 'newly_failing' ? 'bad' : c.category === 'unevaluated' ? 'warn' : undefined;
  const typeWord = c.resource ? typeWordOf(c.resource.resource_type) : null;
  return (
    <li className="sw-change" data-edge={edge} data-change={c.id} data-category={c.category}>
      <div className="sw-change-top">
        <div className="sw-change-main">
          <span className="sw-change-t">
            {statusChip(c)}
            <button type="button" className="sw-chev sw-change-title" aria-expanded={ea} aria-controls={eaId} onClick={() => setEa(!ea)}>
              <span aria-hidden="true">{ea ? '▾' : '›'}</span>{titleOf(c)}<span className="sr-only">{ea ? ': hide expected and actual' : ': show expected and actual'}</span>
            </button>
          </span>
          <span className="sw-change-meta">
            {c.resource ? <b>{c.resource.name}</b> : null}
            {typeWord ? <span>{typeWord}</span> : null}
            {c.resource?.account_ref ? <span>{c.resource.account_ref}</span> : null}
            {c.cause ? <span className="sw-cause" data-cause={c.cause}><span aria-hidden="true">{CAUSE_GLYPH[c.cause]} </span>{CAUSE_LABEL[c.cause]}</span> : null}
          </span>
        </div>
        <div className="sw-change-act">
          {c.category === 'newly_failing' ? <>
            <Button compact variant="secondary" aria-expanded={open} aria-controls={`fix-${c.id}`} onClick={(e) => onFix(e.currentTarget)}>{`Fix ${resName(c)}`}</Button>
            <span className="sw-who">{c.cause === 'rules' ? 'needs your engineering team · our rules changed' : 'needs your engineering team'}</span>
          </> : c.category === 'unevaluated' ? <CouldntCheckFixes items={couldntItems(c, sources)} prefix={prefix} ctx={{ program, ref: c.affects.find((a) => a.program === program)?.ref ?? null }} compact /> : null}
        </div>
      </div>
      {ea ? (
        <dl className="sw-ea" id={eaId}>
          <dt>Expected</dt><dd>{valueText(c.expected)}</dd>
          <dt>Actual</dt><dd>{valueText(c.actual)}</dd>
          <dt>Last sweep</dt><dd>{c.prev_outcome ? (c.prev_outcome === 'pass' ? 'Met' : c.prev_outcome === 'fail' ? 'Not met' : c.prev_outcome === 'not_applicable' ? 'Not applicable' : "Couldn't check") : 'Not checked'}</dd>
          <dt>Affects</dt><dd>{c.affects.length ? c.affects.map((a) => `${programLabel(a.program)} ${a.ref}`).join(' · ') : 'No program control uses this check yet'}</dd>
        </dl>
      ) : null}
      {open ? <FixPanel c={c} prefix={prefix} slug={slug} canSweep={canSweep} announce={announce} onClose={onClose} /> : null}
    </li>
  );
}

function FixPanel({ c, prefix, slug, canSweep, announce, onClose }: { c: SweepChange; prefix: string; slug: string; canSweep: boolean; announce: (m: string) => void; onClose: () => void }) {
  const h = useRef<HTMLHeadingElement>(null);
  useEffect(() => { h.current?.focus(); h.current?.scrollIntoView?.({ block: 'nearest' }); }, []);
  const steps = useMemo(() => (c.remediation ?? '').split(/(?<=\.)\s+/).filter(Boolean), [c.remediation]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(c.remediation ?? ''); announce('Steps copied.'); } catch { announce('Copy is not available here. Select the steps to copy them.'); }
  };
  const qc = useQueryClient();
  // BU-5: a change with no finding offers "Create finding" (the finding the sweep would open, prefilled from
  // this change; Jira / ServiceNow tickets follow from the finding) — never an unfiltered findings list.
  const [made, setMade] = useState<string | null>(null);
  const [making, setMaking] = useState(false);
  const [makeErr, setMakeErr] = useState<string | null>(null);
  const f = c.finding ?? (made ? { finding_id: made, status: 'open', external_ticket_id: null, external_ticket_url: null } : null);
  // Built-UI #7: the hand-off — the finding (where Jira / ServiceNow tickets are made) and its ticket when there is one.
  const findingHref = f ? `${prefix}/findings?finding=${encodeURIComponent(f.finding_id)}` : null;
  const createFinding = async () => {
    if (making) return;
    setMaking(true); setMakeErr(null);
    try {
      const r = await createChangeFinding(slug, c.id);
      setMade(r.finding_id);
      announce(r.created ? `Finding created for ${resName(c)}.` : `${resName(c)} already has an open finding.`);
      void qc.invalidateQueries({ queryKey: sweepKeys.all(slug) });
    } catch (e) { setMakeErr(e instanceof Error ? e.message : 'The finding could not be created.'); }
    finally { setMaking(false); }
  };
  return (
    <div className="sw-fix" id={`fix-${c.id}`} role="region" aria-labelledby={`fixh-${c.id}`} data-esc-owner=""
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } }}>
      <div className="sw-fix-head">
        <h5 className="sw-fix-h" id={`fixh-${c.id}`} ref={h} tabIndex={-1}>{`Fix ${resName(c)}`}</h5>
        <Button compact variant="ghost" onClick={onClose}>Close<span className="sr-only">{`: fix ${resName(c)}`}</span></Button>
      </div>
      <p className="sw-fix-k">Set</p>
      <p>{valueText(c.expected)}</p>
      <p className="sw-fix-k">It is now</p>
      <p>{valueText(c.actual)}</p>
      {steps.length ? <>
        <p className="sw-fix-k">How</p>
        <pre role="region" aria-label={`Steps to fix ${resName(c)}`} tabIndex={0}>{steps.join('\n')}</pre>
      </> : null}
      <span className="gp-actions">
        {steps.length ? <Button compact variant="secondary" onClick={() => void copy()}>Copy the steps</Button> : null}
        {findingHref
          ? <Link className="gp-linkbtn gp-linkbtn--compact" to={findingHref} data-part="open-finding">Open finding<span className="sr-only">{`: ${resName(c)}`}</span></Link>
          : canSweep
            ? <Button compact variant="secondary" data-part="create-finding" aria-disabled={making || undefined} onClick={() => void createFinding()}>Create finding<span className="sr-only">{`: ${resName(c)}`}</span></Button>
            : <AskAdmin slug={slug} compact verb={`open a finding for ${resName(c)}`} what="tracks the fix" subject={`GAC Pro: open a finding for ${resName(c)}`} />}
        {makeErr ? <span className="gp-field-err" role="alert">{makeErr}</span> : null}
        {f?.external_ticket_url && f.external_ticket_id
          ? <a className="sw-textbtn" href={f.external_ticket_url} target="_blank" rel="noreferrer noopener" data-part="ticket">{`Ticket ${f.external_ticket_id}`}<span className="sr-only"> (opens in a new tab)</span></a> : null}
        <span className="sw-who">needs your engineering team</span>
      </span>
      <p className="sw-fix-k">Affects</p>
      <p className="sw-affects">
        {c.affects.length ? c.affects.map((a) => (
          <Link key={`${a.program}-${a.requirement_id}`} to={`${prefix}/programs/${a.program}/controls/${encodeURIComponent(a.requirement_id)}`}>{`${programLabel(a.program)} ${a.ref} · ${a.title}`}</Link>
        )) : <span className="sw-fix-dim">No program control uses this check yet.</span>}
      </p>
      <p className="sw-fix-dim">{'The next sweep checks it again'}{canSweep ? <><span aria-hidden="true">{' · '}</span><SweepNowLink slug={slug} name={resName(c)} announce={announce} /></> : null}</p>
    </div>
  );
}

/** The same on-demand sweep as "Sweep again" (owner / admin); it reads every source, so it says the sweep. */
function SweepNowLink({ slug, name, announce }: { slug: string; name: string; announce: (m: string) => void }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  return <button type="button" className="sw-textbtn" aria-disabled={busy || undefined} onClick={async () => {
    if (busy) return; setBusy(true);
    try { await startSweep(slug); announce(`Sweep started. ${name} is checked again when it finishes.`); await qc.invalidateQueries({ queryKey: sweepKeys.all(slug) }); }
    catch (e) { const s = e instanceof ApiError ? e.status : 0; announce(s === 409 ? 'A sweep is already running.' : s === 429 ? 'You swept less than 15 minutes ago. Try again shortly.' : 'The sweep could not start.'); }
    finally { setBusy(false); }
  }}>{busy ? 'Starting…' : 'Sweep again now'}</button>;
}
